/**
 * @class AccordionItem
 * @extends HTMLElement
 *
 * Self-contained accordion item.
 *
 * Expects:
 *   - Trigger:     [data-ref="trigger"] with aria-controls pointing to a panel ID
 *   - Panel:       [data-ref="panel"]
 *   - Close icon:  [data-ref="icon-close"]
 *   - Open icon:   [data-ref="icon-open"]
 *
 * An `open` attribute rendered on the host (like native `<details open>`)
 * makes the item start expanded - the server can decide the initial state
 * without waiting on JS.
 */
class AccordionItem extends HTMLElement {
  #trigger = null;
  #panel = null;
  #iconClose = null;
  #iconOpen = null;

  connectedCallback() {
    this.#trigger = this.querySelector('[data-ref="trigger"]');
    this.#panel = this.querySelector('[data-ref="panel"]');
    this.#iconClose = this.querySelector('[data-ref="icon-close"]');
    this.#iconOpen = this.querySelector('[data-ref="icon-open"]');

    this.#trigger?.addEventListener('click', this.#onClick);

    this.#syncInitialState();
  }

  #syncInitialState() {
    const isOpen = this.hasAttribute('open');

    this.#trigger?.setAttribute('aria-expanded', String(isOpen));

    if (isOpen) {
      this.#panel?.removeAttribute('inert');
    } else {
      this.#panel?.setAttribute('inert', '');
    }

    this.#iconClose?.classList.toggle('hidden', isOpen);
    this.#iconOpen?.classList.toggle('hidden', !isOpen);
  }

  disconnectedCallback() {
    this.#trigger?.removeEventListener('click', this.#onClick);

    this.#trigger = null;
    this.#panel = null;
    this.#iconClose = null;
    this.#iconOpen = null;
  }

  get #isOpen() {
    return this.#trigger?.getAttribute('aria-expanded') === 'true';
  }

  open() {
    this.#trigger.setAttribute('aria-expanded', 'true');
    this.toggleAttribute('open', true);
    this.#panel.removeAttribute('inert');

    this.#iconClose?.classList.add('hidden');
    this.#iconOpen?.classList.remove('hidden');

    this.closest('accordion-group')?.closeOthers(this);

    this.onOpen();
  }

  close() {
    this.#trigger.setAttribute('aria-expanded', 'false');
    this.toggleAttribute('open', false);
    this.#panel.setAttribute('inert', '');

    this.#iconClose?.classList.remove('hidden');
    this.#iconOpen?.classList.add('hidden');

    this.onClose();
  }

  toggle() {
    this.#isOpen ? this.close() : this.open();
  }

  #onClick = () => this.toggle();

  onOpen() {}
  onClose() {}
}

if (!customElements.get('accordion-item')) {
  customElements.define('accordion-item', AccordionItem);
}

/**
 * @class AccordionGroup
 * @extends HTMLElement
 *
 * Optional wrapper that coordinates accordion item behavior.
 *
 * Attributes:
 *   - data-mode="multiple" - Multiple items can be open simultaneously (default)
 *   - data-mode="single"   - Only one item open at a time
 */
class AccordionGroup extends HTMLElement {
  get #mode() {
    return this.dataset.mode === 'single' ? 'single' : 'multiple';
  }

  closeOthers(caller) {
    if (this.#mode === 'multiple') return;

    this.querySelectorAll('accordion-item').forEach((item) => {
      if (item !== caller) item.close();
    });
  }
}

if (!customElements.get('accordion-group')) {
  customElements.define('accordion-group', AccordionGroup);
}

class DisclosureItem extends AccordionItem {
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    this.close();
    this.querySelector('[data-ref="trigger"]')?.focus();
  };

  #onDocumentClick = (event) => {
    if (!this.contains(event.target)) this.close();
  };

  onOpen() {
    document.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener('click', this.#onDocumentClick);
  }

  onClose() {
    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('click', this.#onDocumentClick);
  }
}

if (!customElements.get('disclosure-item')) {
  customElements.define('disclosure-item', DisclosureItem);
}
